import React from 'react';
import type {MouseEvent} from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Spacer, spacing} from '@nfq/react-grid';

import {CheckLight} from '../../../icons/CheckLight';
import {P} from '../../../layout/Text/Text';

import {useOptionItem} from './useOptionItem';

/**
 * The `ComponentProps` interface defines the properties for the `OptionItem` component,
 * which represents a selectable item within a dropdown or list. This interface includes
 * properties for customizing the item's appearance and behavior, such as whether the item
 * is active, selectable, or rendered with smaller dimensions.
 */
export interface ComponentProps {
    /**
     * The index or unique identifier of the option item, which helps identify the item
     * in a list of options. This can be a number or string.
     */
    index?: number | string;
    /**
     * A boolean flag indicating whether the option item is currently active or selected.
     * When `true`, the item will be visually styled as active.
     *
     * @default false
     */
    isActive?: boolean;
    /**
     * A boolean flag indicating whether the option item is selectable. When `true`, the item
     * will display a checkmark when selected, or a dummy placeholder when not selected.
     *
     * @default false
     */
    isSelectable?: boolean;
    /**
     * The label text to display inside the option item.
     */
    label: string;
    /**
     * Event handler that is triggered when the option item is clicked. It passes
     * the `MouseEvent` as an argument.
     */
    onClick?(event: MouseEvent<HTMLDivElement>): void;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'OptionItem'
     */
    testId?: string;
}

/**
 * Renders an interactive option item for a dropdown or selection list.
 * It displays the provided label, and when `isSelectable` is true, a checkmark or dummy is shown to indicate the selection state.
 * The item can also be styled as active, with different colors applied based on the current state.
 *
 * @param props              The component props.
 * @param props.index        The index of the option item, used to identify the item within the list.
 * @param props.isActive     A boolean flag indicating if the option item is currently active or selected.
 * @param props.isSelectable A boolean flag indicating if the option item is selectable, which shows a checkmark when selected.
 * @param props.isSmall      A boolean flag that reduces the size of the option item when set to true.
 * @param props.label        The label to be displayed inside the option item.
 * @param props.onClick      Event handler for when the option item is clicked.
 * @param props.testId       The test id used to uniquely identify the component in testing.
 * @returns A JSX element representing an individual option item.
 *
 * @example
 * ```tsx
 * <OptionItem
 *   index="1"
 *   isActive={true}
 *   isSelectable={true}
 *   label="Option 1"
 *   onClick={handleClick}
 * />
 * ```
 */
const OptionItem = ({
    index,
    isActive = false,
    isSelectable = false,
    label,
    onClick = () => {},
    testId = 'OptionItem'
}: ComponentProps) => {
    const {colors, itemColor} = useOptionItem(isActive);

    return (
        <Wrapper $isActive={isActive} data-cy={testId} data-index={index} data-is-active={isActive} onClick={onClick}>
            <P data-cy={`${testId}-label`}>
                <Color $color={itemColor}>{label}</Color>
            </P>
            {isSelectable && (
                <>
                    <Spacer x={2} isNotStretching />
                    {isActive && (
                        <CheckLight color1={colors.primary} height={16} testId={`${testId}-check`} width={16} />
                    )}
                    {!isActive && (
                        <Dummy />
                    )}
                </>
            )}
        </Wrapper>
    );
};

OptionItem.displayName = 'OptionItem';

export {OptionItem};

interface WrapperProps {
    $isActive: boolean;
}

const Wrapper = styled.div<WrapperProps>`
    align-items: center;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    padding: ${spacing(4)} ${spacing(3)};
    width: 100%;

    &:hover {
        background-color: ${({theme}) => theme.colors.textSecondaryInv};
    }
`;

const Dummy = styled.div`
    height: 16px;
    width: 16px;
`;